import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { get } from 'lodash';
import { useScenariosStore } from '@/stores/scenarios';
import { useContextStore } from '@/stores/context';
import sectionStatuses from '@enums/section-status';

// eslint-disable-next-line no-unused-vars
export function useScenarioStatusFormatter(_currentSection = null) {
  const scenariosStore = useScenariosStore();
  const contextStore = useContextStore();
  const { selectedScenarioStatus } = storeToRefs(scenariosStore);

  // Feature flags that control which sections are visible
  const regionsByAttributeEnabled = computed(() =>
    get(contextStore.clientConfig, 'features.regionsByAttributeEnabled', false)
  );

  const switchingModellingEnabled = computed(() =>
    get(contextStore.clientConfig, 'features.switchingModellingEnabled', false)
  );

  const finalisationEnabled = computed(() =>
    get(contextStore.clientConfig, 'features.finalisationEnabled', false)
  );

  const formatStatus = status => {
    switch (status) {
      case sectionStatuses.complete:
        return 'Complete';
      case sectionStatuses.inProgress:
        return 'In Progress';
      case sectionStatuses.failed:
        return 'Failed';
      case sectionStatuses.notStarted:
      default:
        return 'Not Started';
    }
  };

  const formatScenarioProgress = computed(() => {
    const status = selectedScenarioStatus.value;
    if (!status) return null;

    const result = {};

    // Space
    if (status.space !== undefined) {
      result.space = formatStatus(status.space?.status || sectionStatuses.notStarted);
    }

    // Inputs
    if (status.inputs !== undefined) {
      const inputsObj = {
        status: formatStatus(status.inputs?.status || sectionStatuses.notStarted),
      };
      if (status.inputs.attributeEditor?.status)
        inputsObj.attributeEditor = formatStatus(status.inputs.attributeEditor.status);
      if (status.inputs.cannGroups?.status)
        inputsObj.cannGroups = formatStatus(status.inputs.cannGroups.status);
      if (status.inputs.varietyGroups?.status)
        inputsObj.varietyGroups = formatStatus(status.inputs.varietyGroups.status);
      result.inputs = inputsObj;
    }

    // Measuring
    if (status.measuring !== undefined) {
      if (!status.measuring.available) {
        result.measuring = { available: false, lockedReason: 'requires CANN Groups complete' };
      } else {
        const measuringObj = {
          available: true,
          status: formatStatus(status.measuring.status || sectionStatuses.notStarted),
        };
        if (status.measuring.switching?.status)
          measuringObj.switching = formatStatus(status.measuring.switching.status);
        if (status.measuring.cdt?.status)
          measuringObj.cdt = formatStatus(status.measuring.cdt.status);
        if (regionsByAttributeEnabled.value && status.measuring.region?.status) {
          measuringObj.region = formatStatus(status.measuring.region.status);
        }
        if (status.measuring.clustering?.status)
          measuringObj.clustering = formatStatus(status.measuring.clustering.status);
        result.measuring = measuringObj;
      }
    }

    // Modelling
    if (status.modelling !== undefined) {
      if (!status.modelling.available) {
        result.modelling = { available: false, lockedReason: 'requires CDT complete' };
      } else {
        const modelObj = {
          available: true,
          status: formatStatus(status.modelling.status || sectionStatuses.notStarted),
        };
        if (status.modelling.productModelling?.status)
          modelObj.productModelling = formatStatus(status.modelling.productModelling.status);
        if (switchingModellingEnabled.value && status.modelling.switchingModelling?.status) {
          modelObj.switchingModelling = formatStatus(status.modelling.switchingModelling.status);
        }
        result.modelling = modelObj;
      }
    }

    // Assortment
    if (status.assortment !== undefined) {
      if (!status.assortment.available) {
        result.assortment = { available: false, lockedReason: 'requires CDT complete' };
      } else {
        result.assortment = {
          available: true,
          status: formatStatus(status.assortment.status || sectionStatuses.notStarted),
        };
      }
    }

    // Execution
    if (status.execution !== undefined) {
      const execObj = {
        status: formatStatus(status.execution.status || sectionStatuses.notStarted),
      };
      if (finalisationEnabled.value && status.finalisation) {
        execObj.finalisation = formatStatus(
          status.finalisation.status || sectionStatuses.notStarted
        );
      }
      result.execution = execObj;
    }

    return result;
  });

  return {
    formatScenarioProgress,
    selectedScenarioStatus,
    // Export feature flags in case components need them
    regionsByAttributeEnabled,
    switchingModellingEnabled,
    finalisationEnabled,
  };
}
